/**
 * 派送小票。
 *
 * ★ 这张纸贴在包裹上,它要回答的只有一个问题:**这箱东西该送到谁手上**。
 *   所以姓名、电话、地址占最大的字号,别的都让位。
 *
 * ★ 特殊物品和代收现金单独反白 —— 这两样漏看的代价和别的不是一个量级:
 *   冻品在车上化了、代收的钱没收着回来就要不回来了。
 *   混在正文里印成同样大小,等于没印。
 *
 * ★ 这一层**不碰打印机**:进来是订单,出去是一串「打印行」。
 *   蓝牙那条路把它画成图再发 ESC/POS,系统打印那条路把它排成 HTML,
 *   两条路印出来的东西必须是同一张 —— 所以内容只在这里定义一次。
 */

import { QR_DOTS, qrModules, type ReceiptQrConfig } from './qr';

export * from './qr';

/** `off` = 这一块**不印**。留空比印一行没人看的字有用 —— 纸是有限的 */
export type ReceiptSize = 'off' | 'small' | 'normal' | 'big' | 'huge' | 'giant';
export type ReceiptAlign = 'left' | 'center';

/**
 * 票面上的几块。
 *
 * ★ 分块是为了让字号**按块**调:同一叠票在不同店里的用法不一样 ——
 *   有人代收多,要「代收」那行隔着几米就看得见;
 *   有人地址长,宁可把地址放大、别的缩掉。
 *   给一个统一的「字号大中小」是答不了这件事的。
 */
export type ReceiptSlot =
  /** 商家的打印别名 —— 印在最上面,分拣时一眼看出这箱是谁家的 */
  | 'shop'
  | 'name'
  | 'phone'
  | 'address'
  | 'special'
  | 'cod'
  | 'count'
  | 'remark'
  | 'orderNo'
  | 'zone'
  | 'time'
  /** 底部留白 —— 给手写的地方。「字号」在这一块的含义是「留多高」 */
  | 'tail'
  /**
   * 商家的二维码 —— 印在别名**上面**,整张票的最后那一块。
   *
   * ★ 「字号」在这一块的含义是「印多大」(见 qr.ts 的 QR_DOTS)。
   *   和别的块一样能单独关掉:关掉就一个点都不印。
   */
  | 'qr';

/** 每块的字号。没设的那块用默认 */
export type ReceiptSizes = Partial<Record<ReceiptSlot, ReceiptSize>>;

/**
 * 默认字号。
 *
 * ★ 这一套是照着**真的打出来的小票**定的,不是拍脑袋:
 *   姓名电话大、地址标准、代收和特殊物品大(漏看会出事的那两样)、
 *   件数大(仓库点货看的就是它),单号区域时间小 ——
 *   它们是事后查的东西,不是路上要用的。
 */
export const DEFAULT_RECEIPT_SIZES: Record<ReceiptSlot, ReceiptSize> = {
  /*
   * ★ 商家别名默认就印(只要那家配了)。
   *   它是**分拣**用的:一堆框子堆在一起,先分清哪几箱是同一家的,
   *   再看送给谁。没配别名的商家一个字都不会多印,所以默认打开
   *   对现在这些票面没有任何影响。
   */
  shop: 'big',
  name: 'big',
  phone: 'big',
  address: 'normal',
  special: 'big',
  cod: 'big',
  count: 'big',
  remark: 'normal',
  /*
   * ★ 单号、区域、时间默认**不印**。
   *   它们是事后查的东西,不是路上要用的 —— 而这张纸的地方要留给
   *   地址和手写。要查的时候在管理端搜收件人一样找得到。
   *   (真要印,设置里一个下拉就打开。)
   */
  orderNo: 'off',
  zone: 'off',
  time: 'off',
  /** 底部留白:默认中等(约九行)—— 够写一个金额加一句话 */
  tail: 'big',
  /*
   * ★ 二维码默认**不印**。
   *
   *   要印得同时满足两件事:商家资料里填了内容(填什么、图还是字符串),
   *   **而且**打印弹窗里把这一块打开(选一个尺寸)。缺一样都不印。
   *
   *   用户原话:「是否打印二维码是由打印时候弹出的小窗口来决定的,
   *   商家设置里面只负责填写二维码相关数据,如果设置了打印并且填写了
   *   才打印」。
   *
   *   两件事分开是有道理的:**填什么**是商家的属性(那是他的收款码、
   *   他的公众号),而**这叠票上印不印**是眼下这一次打印的事 ——
   *   同一个商家,贴货架的票要印码,内部留存那张不需要。
   *   把开关放在商家资料里的话,他得先去改商家、打完再改回来。
   *
   *   所以默认是「不印」:填了内容不等于每张票都要印。
   *   开一次之后存在店里(和别的字号一样),以后就一直印。
   */
  qr: 'off',
};

export interface ReceiptLine {
  text: string;
  /** 这一行属于哪一块 —— 界面上按块调字号时要认得出来 */
  slot?: ReceiptSlot;
  size?: ReceiptSize;
  align?: ReceiptAlign;
  bold?: boolean;
  /**
   * 这一行要显眼。
   *
   * ★ 曾经是「白字黑底」,而热敏纸上那是**看不见** ——
   *   浏览器打印默认不印背景,黑底被丢掉,只剩白字印在白纸上;
   *   而那两行恰恰是「冻品」和「代收现金」。
   *   后来改成粗黑框,用户看了实物说框也不要 ——
   *   现在就是**加粗的黑字**,靠字号站出来。这是在真实的小票机上
   *   唯一一种不会消失的强调。
   */
  strong?: boolean;
  /**
   * 这一行要画在边框里。
   *
   * ★ 备注和区域那几行加个框 —— 框起来之后和上下文分得清楚,
   *   司机扫一眼就知道「框里的是备注」,不用从头到尾读一遍。
   */
  bordered?: boolean;
  /** 这一行是分隔线,text 忽略 */
  rule?: boolean;
  /** 空行 */
  blank?: boolean;
  /**
   * 这一行是二维码,`text` 忽略。
   *
   * ★ 两个字段**二选一**,由 `buildReceipt` 按商家配的方式填好一个:
   *   `modules` 来自「字符串」那条路(现编的矩阵),
   *   `image` 来自「上传图片」那条路(内联的 data: URI)。
   *
   * ★ 为什么在这一层就定死成这两样:这张票有四条打印路径,
   *   其中浏览器打印台(station.html)是 ES5、零依赖、要在 Win7 上跑的。
   *   给它一个「内容字符串」让它自己编码是做不到的;
   *   给它一格一格的矩阵或者一张图,它用 innerHTML 就画得出来。
   */
  qr?: ReceiptQrLine;
}

/**
 * 票面上那个二维码,已经是**画得出来**的形态。
 *
 * ★ `box` 跟着一起发,不让各条渲染路各自去查尺寸表 ——
 *   四条路里有一条(station.html)根本 import 不了这个包,
 *   尺寸表在那儿抄一份的话,改了这边忘了那边,
 *   表现是「预览和纸上不一样大」,而没人会想到去对表。
 */
export interface ReceiptQrLine {
  /** 矩阵,每行一串 '0'/'1','1' 是黑。和 image 二选一 */
  modules?: string[];
  /** 图片 data: URI。和 modules 二选一 */
  image?: string;
  /** 印多大 —— 边长,单位是打印点(203dpi,8 点 = 1mm) */
  box: number;
}

export interface ReceiptOrder {
  orderNo: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  /** 门牌 / 楼层 —— 地址里常常不含这一段,而它决定司机要不要上楼 */
  receiverNote?: string | null;
  packageCount: number;
  /** 最小货币单位(分)。0 表示不代收 */
  codAmount: number;
  /**
   * 要代收,但下单时还不知道收多少。
   *
   * ★ 这张小票要在**算出金额之前**就打出来贴在框上 ——
   *   它是拿来认「这框是谁的单」的。所以金额那儿留一条空线,
   *   等金额出来手写补上。
   */
  codPending?: boolean;
  currency: string;
  specialItems?: ReadonlyArray<{ code: string; label: string; icon?: string | null }>;
  /**
   * 商家的打印别名 —— 商家资料里配的那个。空就不印。
   *
   * ★ 不用商家全称:台账里是「XX 贸易有限公司」,而贴在框上要的是
   *   分拣时一眼认出来的那几个字(「三号仓」「阿 May」)。
   */
  shopAlias?: string | null;
  /**
   * 商家的小票二维码 —— 商家资料里配的那个。空就不印。
   *
   * ★ 和别名一样按商家走,不按租户:一个平台上的商家各有各的收款码 /
   *   各自的公众号,印成同一个就等于印错。
   */
  shopQr?: ReceiptQrConfig | null;
  /** 派给谁了 —— 现在不印(见下面),留着是别的地方在用 */
  driverName?: string | null;
  /**
   * 物品 / 备注 —— 建单弹窗里那个框写进的就是它。
   *
   * ★ 系统里「备注」有三处:收件备注(贴着地址印)、这个、还有内部备注。
   *   人不会记得自己刚才写的那句话进了哪一个字段,他只记得「我写了备注」——
   *   所以三处都要印。用户报的就是这个:写了备注,票上没有。
   */
  itemDescription?: string | null;
  /** 内部备注 —— 导入的表格里那一列「备注」落在这儿 */
  remark?: string | null;
  zoneName?: string | null;
  createdAt?: string | null;
}

export interface ReceiptOptions {
  /** 打印时间,由调用方传 —— 这一层不读时钟,好测 */
  now: Date;
  /** 语言环境,决定日期怎么写 */
  locale?: string;
  timeZone?: string;
  /** 按块调过的字号 */
  sizes?: ReceiptSizes;
}

/**
 * 底部留白留几行 —— 「字号」在那一块的含义是「留多高」。
 *
 * ★ 整套在 2026-09-21 统一放大到原来的 1.5 倍。
 *   用户报的是「选了超大也没多大」:原先最大那档 16 行,
 *   按渲染层一行手写 28 点算约 450 点 —— 80mm 纸上看着只是一小段,
 *   写不下「金额 + 一句话」。各档同比例放大,而不是只动最大那一档:
 *   只动一档的话,原来选「大」的店会发现自己那档没变,
 *   而他们抱怨的是同一件事。
 */
const TAIL_LINES: Record<ReceiptSize, number> = {
  off: 0,
  small: 3,
  normal: 6,
  big: 9,
  huge: 15,
  giant: 24,
};

/** 分 → 「$25.00」。不做货币符号猜测,认不出就把代码摆上去 */
export function formatReceiptMoney(minorAmount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency.toUpperCase()];
  const value = (minorAmount / 100).toFixed(2);
  return symbol ? `${symbol}${value}` : `${value} ${currency.toUpperCase()}`;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  NZD: '$',
  AUD: '$',
  USD: '$',
  CNY: '¥',
  HKD: 'HK$',
  GBP: '£',
  EUR: '€',
};

function stamp(d: Date, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/**
 * 一张小票的内容。
 *
 * ★ 顺序是照着**司机拿起包裹的动作**排的:先看送给谁(姓名),
 *   再看往哪儿打电话,再看去哪儿。单号排在最后 ——
 *   它是出了事之后查的东西,不是路上要用的。
 */
export function buildReceipt(o: ReceiptOrder, opts: ReceiptOptions): ReceiptLine[] {
  const locale = opts.locale ?? 'zh-CN';
  const size = (slot: ReceiptSlot): ReceiptSize =>
    opts.sizes?.[slot] ?? DEFAULT_RECEIPT_SIZES[slot];
  const lines: ReceiptLine[] = [];

  lines.push({
    text: o.receiverName,
    slot: 'name',
    size: size('name'),
    align: 'center',
    bold: true,
  });
  lines.push({ text: o.receiverPhone, slot: 'phone', size: size('phone'), align: 'center' });
  lines.push({ rule: true, text: '' });
  lines.push({ text: o.receiverAddress, slot: 'address', size: size('address') });

  /*
   * ★ 这两样单独一块,而且反白。
   *   「冻品」印成和地址一样大的一行字,司机扫一眼小票是不会注意到的 ——
   *   而他注意不到的后果是这箱东西在车上待两个小时。
   */
  const special = (o.specialItems ?? []).filter((s) => s.label.trim() !== '');
  const hasCod = o.codAmount > 0 || o.codPending === true;
  if (special.length > 0 || hasCod) {
    lines.push({ rule: true, text: '' });
    if (special.length > 0) {
      /*
       * ★ 这里**不带 icon**。
       *   小票机是单色热敏,彩色 emoji 印出来是一团黑;
       *   而反白那一条是白字黑底 —— emoji 不吃颜色设置,
       *   印在黑底上等于什么都没有。名字本来就够认了。
       */
      lines.push({
        text: special.map((s) => s.label).join('   '),
        slot: 'special',
        size: size('special'),
        align: 'center',
        bold: true,
        strong: true,
      });
    }
    if (hasCod) {
      /*
       * ★ 待定的印成「代收现金 ______」。
       *   印 $0.00 的话,司机看到的是「这单不用收钱」——
       *   而这单恰恰是要收的,只是数还没出来。
       *   空线是给人手写的:纸已经贴在框上了,补一笔就行。
       */
      lines.push({
        text: o.codPending
          ? '代收现金 ____________'
          : `代收现金 ${formatReceiptMoney(o.codAmount, o.currency)}`,
        slot: 'cod',
        size: size('cod'),
        align: 'center',
        bold: true,
        strong: true,
      });
    }
  }

  /*
   * ★ 单号、件数、区域、时间**各成一块**,而不是挤在一行。
   *   挤在一行的话它们只能共用一个字号 —— 而仓库要的是
   *   「件数大到点货时一眼看见、单号小到不占地方」。
   *   每一块都能单独关掉:关掉的那块一行都不印。
   */
  lines.push({ rule: true, text: '' });
  lines.push({ text: `单号 ${o.orderNo}`, slot: 'orderNo', size: size('orderNo') });
  lines.push({ text: `${o.packageCount} 件`, slot: 'count', size: size('count') });
  /*
   * ★ 备注和物品描述放在一起,用边框框起来。
   *
   *   系统里「备注」和「物品」来自三处:建单填的备注(receiverNote)、
   *   物品描述(itemDescription)、导入或内部备注(remark)。
   *   全部收拢到这一块,并用黑线边框框起来,分拣和派送时一眼看清。
   *   按换行拆成多行,一样的话只印一遍。
   */
  const notes: string[] = [];
  for (const t of [o.itemDescription, o.receiverNote, o.remark]) {
    if (!t) continue;
    for (const piece of t.split(/\r?\n/)) {
      const v = piece.trim();
      if (v && !notes.includes(v)) notes.push(v);
    }
  }
  for (const n of notes) {
    lines.push({ text: n, slot: 'remark', size: size('remark'), bordered: true });
  }
  if (o.zoneName) lines.push({ text: o.zoneName, slot: 'zone', size: size('zone') });
  /*
   * ★ 不印司机名字。
   *
   *   这张纸是打好贴在框上的,而那会儿**还没派单** —— 印上去的要么是空,
   *   要么是后来改派之前的那个人。而纸上写着张三、车上的是李四,
   *   比不写更坏:出了事对着这张纸查,查到的是没碰过这箱货的人。
   *   (真要按司机分堆,靠的是管理端上的筛选,不是这张纸。)
   */
  lines.push({ text: stamp(opts.now, locale, opts.timeZone), slot: 'time', size: size('time') });

  /*
   * ★ 末尾留白,给人手写。
   *
   *   代收金额待定的那些单,数是后面才知道的 —— 而纸已经贴在框上了。
   *   除此之外,仓库里本来就会往票上补东西(「放门口」「先别送」)。
   *   不留的话,他们写在字上面,而那一行原本是地址。
   *
   * ★ 留多少**可调**。这件事没有一个对所有店都对的数:
   *   代收多的店要写金额,别的店只写一两个字。
   *   所以「字号」在这一块的含义是「留多高」。
   */
  const tail = TAIL_LINES[size('tail')];
  for (let i = 0; i < tail; i++) {
    lines.push({ blank: true, text: '' });
  }
  /*
   * ★ 留白的**最下面**放一条线。
   *
   *   用户报的:「底部的留白空间,不管设置多大,好像都打印不出来」。
   *   而排出来的文档里那些空白**是在的**(量过:高度从 143px 涨到 445px)——
   *   是打印机把末尾的空白裁掉了:热敏机打完最后一点墨就走纸切纸,
   *   后面那段白纸对它来说没有意义。
   *
   *   所以在最下面放一条线:有墨在那儿,上面那段空白就必须被送出来。
   *   顺带它还是一条「写到这儿为止」的边界。
   */
  /*
   * ★ 商家别名印在**留白下面**,整张票的最后 —— 位置是用户定的。
   *
   *   上面那几行是司机送货时要读的(送给谁、往哪儿打电话、去哪儿),
   *   中间那块空白是留给手写的;别名是**分拣**时才看的一眼,
   *   摆在最下面既不挤着前面,也不占掉手写的地方。
   *
   *   顺带:它自己就是「末尾有墨」,那段空白因此不会被打印机裁掉
   *   (见下面那条线的说明)。
   */
  /*
   * ★ 二维码印在别名**上面**,底部留白**下面**。
   *
   *   位置不是随便定的:上面那几行是司机送货时读的(送给谁、往哪儿打
   *   电话、去哪儿),中间那块空白是留给手写的。二维码和别名一样
   *   属于「事后才看的一眼」,摆在最下面既不挤着前面,
   *   也不占掉手写的地方。
   *
   *   别名在二维码**下面**:扫码的人对着码,手指落下去正好压住的是
   *   码下面那一行 —— 那儿放店名,他不用挪手就知道扫的是谁家的。
   *
   * ★ 编不出来就**不印**,不抛。商家把二维码内容填成了一串超长文本、
   *   或者上传的图坏了,代价不该是这一叠票全打不出来 ——
   *   纸上少一个码,人当场看得见;整批打印失败,他只知道「打印坏了」。
   */
  const qrCfg = o.shopQr;
  const qrSize = size('qr');
  if (qrCfg && qrSize !== 'off') {
    const box = QR_DOTS[qrSize] ?? 0;
    let qr: ReceiptQrLine | null = null;
    if (qrCfg.mode === 'TEXT') {
      const modules = qrModules(qrCfg.text);
      if (modules) qr = { modules, box };
    } else if (qrCfg.mode === 'IMAGE' && qrCfg.image.startsWith('data:image/')) {
      qr = { image: qrCfg.image, box };
    }
    if (qr && box > 0) {
      lines.push({ text: '', slot: 'qr', size: qrSize, align: 'center', qr });
    }
  }

  if (o.shopAlias?.trim()) {
    lines.push({
      text: o.shopAlias.trim(),
      slot: 'shop',
      size: size('shop'),
      align: 'center',
      bold: true,
    });
  }
  if (tail > 0) lines.push({ rule: true, text: '' });

  /*
   * ★ 关掉的块在这里一次性滤掉,而不是每处写一遍 `if` ——
   *   漏掉一处的表现是「我明明关了它还在印」,而那一处会很难找。
   */
  return lines.filter((l) => l.size !== 'off');
}

// ───────────────────────── ESC/POS ─────────────────────────

/**
 * 小票机的可打印宽度(点)。
 *
 * ★ 58mm 的纸可打印 48mm、80mm 的可打印 72mm,按 203dpi(8 点/mm)算就是
 *   384 和 576 点。这两个数还必须是 8 的倍数 ——
 *   光栅位图一个字节装 8 个点,除不尽的话每一行都会错位,
 *   印出来是一张斜的图。
 */
export const PAPER_DOTS = { '58': 384, '80': 576 } as const;
export type PaperWidth = keyof typeof PAPER_DOTS;

/**
 * 把整张小票**画成图**再发给打印机,而不是发文字。
 *
 * ★ 为什么走图:ESC/POS 的文字模式要按打印机内置码表发字节,中文得转
 *   GB18030,而不同机器的码表页还不一样 —— 换一台机器就可能满屏乱码。
 *   图没有这个问题:屏幕上看到什么,纸上就是什么,字体也由我们说了算。
 *
 * @param bits 每行按 8 点打包好的位图,1 = 黑
 */
export function escposRaster(
  bits: Uint8Array,
  widthDots: number,
  heightDots: number,
  opts: { cut?: boolean; feedLines?: number } = {},
): Uint8Array {
  if (widthDots % 8 !== 0) throw new Error(`打印宽度必须是 8 的倍数,收到 ${widthDots}`);
  const bytesPerRow = widthDots / 8;
  if (bits.length !== bytesPerRow * heightDots) {
    throw new Error(`位图大小对不上:应该是 ${bytesPerRow * heightDots} 字节,收到 ${bits.length}`);
  }

  const out: number[] = [];
  out.push(0x1b, 0x40); // ESC @ 初始化

  /*
   * ★ 一次 GS v 0 最多 255 行,所以长票要切成几段发。
   *   不切的话高度字段溢出,打印机要么吐一段乱码要么什么都不印。
   */
  const CHUNK = 255;
  for (let y = 0; y < heightDots; y += CHUNK) {
    const rows = Math.min(CHUNK, heightDots - y);
    out.push(0x1d, 0x76, 0x30, 0x00); // GS v 0, 普通模式
    out.push(bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff);
    out.push(rows & 0xff, (rows >> 8) & 0xff);
    for (let i = 0; i < rows * bytesPerRow; i++) out.push(bits[y * bytesPerRow + i]!);
  }

  // 走纸:不走的话最后几行还压在打印头下面,撕不下来
  for (let i = 0; i < (opts.feedLines ?? 4); i++) out.push(0x0a);
  if (opts.cut !== false) out.push(0x1d, 0x56, 0x42, 0x00); // GS V B 切纸(没刀的机器会忽略)

  return new Uint8Array(out);
}

/**
 * 灰度像素 → 每行 8 点打包的单色位图。
 *
 * @param gray 每个像素一个字节,0 黑 255 白
 * @param threshold 低于这个值算黑点
 */
export function packMono(
  gray: Uint8Array,
  width: number,
  height: number,
  threshold = 160,
): Uint8Array {
  if (width % 8 !== 0) throw new Error(`宽度必须是 8 的倍数,收到 ${width}`);
  const bytesPerRow = width / 8;
  const out = new Uint8Array(bytesPerRow * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (gray[y * width + x]! < threshold) {
        // 每个字节的最高位是最左边那个点
        out[y * bytesPerRow + (x >> 3)]! |= 0x80 >> (x & 7);
      }
    }
  }
  return out;
}
